import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { customSelectStylesLarge } from '../../../config/selectStyles';
import { showMessage } from '../../../scripts/showMessage.js';
import { Building2, MapPin, Phone, Mail, CreditCard, Receipt } from 'lucide-react';
import { useCityLookup } from '../../../hooks/useCityLookup';
import { CityOptions } from '../../ui/Forms/CityOptions';

const cardStyle = {
    border: '1px solid var(--border-light, #e2e8f0)',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(44, 62, 80, 0.04)',
    marginBottom: '1.5rem',
    background: 'var(--bg-card, #ffffff)'
};

const sectionHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
    paddingBottom: '0.75rem',
    borderBottom: '1px solid var(--border-light, #e2e8f0)',
    color: 'var(--text-primary, #2c3e50)',
    fontWeight: '600',
    fontSize: '1rem'
};

const labelStyle = {
    display: 'block',
    marginBottom: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: 'var(--text-secondary, #64748b)'
};

const inputStyle = {
    width: '100%',
    padding: '0.625rem 0.875rem',
    border: '1px solid var(--border-medium, #cbd5e1)',
    borderRadius: '8px',
    fontSize: '0.9375rem',
    color: 'var(--text-primary, #2c3e50)',
    background: 'var(--bg-card, #ffffff)',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    outline: 'none'
};

const requiredInputStyle = (value) => ({
    ...inputStyle,
    borderColor: !value ? 'var(--danger, #dc3545)' : 'var(--border-medium, #cbd5e1)'
});

const genderOptions = [
    { value: 0, label: '- - -' },
    { value: 1, label: 'Herr' },
    { value: 2, label: 'Frau' }
];

const countryOptions = [
    { value: 1, label: 'Deutschland' },
    { value: 2, label: 'Österreich' },
    { value: 3, label: 'Schweiz' },
    { value: 5, label: 'Italien' }
];

const taxObligationOptions = [
    { value: 1, label: 'Ja' },
    { value: 2, label: 'Nein' }
];

const StepBasedata = ({ initialData, titles: initialTitles, onComplete }) => {
    const [isSaving, setIsSaving] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [titles, setTitles] = useState([]);

    const [formData, setFormData] = useState({
        title: null,
        gender: null,
        surname: '',
        name: '',
        company: '',
        street: '',
        houseNumber: '',
        zipCode: '',
        city: '',
        country: null,
        phone: '',
        fax: '',
        mobil: '',
        email: '',
        emailWedding: '',
        protocol: 'https',
        web: '',
        taxNumber: '',
        vatNumber: '',
        taxObligation: null,
        bank: '',
        iban: '',
        bic: '',
        accountHolder: '',
        notes: ''
    });

    useEffect(() => {
        if (initialData) {
            const titleOptions = [
                { value: 0, label: '- - -' },
                ...(initialTitles || []).map(t => ({ value: t.value, label: t.label }))
            ];
            setTitles(titleOptions);

            let protocol = 'https';
            let webUrl = initialData.web || '';
            if (webUrl.startsWith('http://')) {
                protocol = 'http';
                webUrl = webUrl.replace('http://', '');
            } else if (webUrl.startsWith('https://')) {
                webUrl = webUrl.replace('https://', '');
            }

            setFormData({
                ...initialData,
                title: titleOptions.find(t => t.value === initialData.title) || titleOptions[0],
                gender: genderOptions.find(g => g.value === initialData.gender) || genderOptions[0],
                country: countryOptions.find(c => c.value === initialData.country) || countryOptions[0],
                taxObligation: taxObligationOptions.find(t => t.value === initialData.taxObligation) || taxObligationOptions[0],
                protocol,
                web: webUrl,
                notes: initialData.notes || ''
            });
        }
    }, [initialData, initialTitles]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const setCityFromLookup = useCallback((ort) => {
        setFormData(prev => {
            if (prev.city && prev.city.trim() !== '') return prev;
            return { ...prev, city: ort };
        });
    }, []);

    const { cityOptions, selectCityOption } = useCityLookup(formData.zipCode, formData.country?.value, formData.city, setCityFromLookup);

    const isValid = () => {
        return formData.surname && formData.name && formData.company && formData.email;
    };

    const handleSubmit = async () => {
        setShowValidation(true);

        if (!isValid()) {
            showMessage('Bitte alle Pflichtfelder ausfüllen', 'error');
            return;
        }

        setIsSaving(true);

        try {
            const fullWebUrl = formData.web ? `${formData.protocol}://${formData.web}` : '';

            const submitData = new FormData();
            submitData.append('title', formData.title?.value || 0);
            submitData.append('anrede', formData.gender?.value || 0);
            submitData.append('surname', formData.surname || '');
            submitData.append('name', formData.name || '');
            submitData.append('companyname', formData.company || '');
            submitData.append('street', formData.street || '');
            submitData.append('housenumber', formData.houseNumber || '');
            submitData.append('postcode', formData.zipCode || '');
            submitData.append('city', formData.city || '');
            submitData.append('country', formData.country?.value || 1);
            submitData.append('phonenumber', formData.phone || '');
            submitData.append('fax', formData.fax || '');
            submitData.append('mobil', formData.mobil || '');
            submitData.append('email', formData.email || '');
            submitData.append('email2', formData.emailWedding || '');
            submitData.append('web', fullWebUrl);
            submitData.append('tax', formData.taxNumber || '');
            submitData.append('ustid', formData.vatNumber || '');
            submitData.append('mwst', formData.taxObligation?.value || 1);
            submitData.append('bank', formData.bank || '');
            submitData.append('iban', formData.iban || '');
            submitData.append('bic', formData.bic || '');
            submitData.append('accountHolder', formData.accountHolder || '');
            submitData.append('privacy', '');
            submitData.append('agb', '');
            submitData.append('notes', formData.notes || '');

            const response = await fetch('/index.php/settings/basedata_update', {
                method: 'POST',
                body: submitData
            });

            const data = await response.json();

            if (data.success) {
                showMessage('Stammdaten erfolgreich gespeichert', 'success');
                onComplete();
            } else {
                showMessage('Fehler beim Speichern der Stammdaten', 'error');
            }
        } catch (error) {
            console.error('Error saving basedata:', error);
            showMessage('Fehler beim Speichern der Stammdaten', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const RequiredMark = () => (
        <span style={{ color: 'var(--danger, #dc3545)', marginLeft: '2px' }}>*</span>
    );

    return (
        <div style={{ padding: '1.5rem' }}>
            <div className="row">
                {/* Left Column */}
                <div className="col-lg-8">
                    {/* Firma & Person */}
                    <div className="card" style={cardStyle}>
                        <div className="card-body">
                            <div style={sectionHeaderStyle}>
                                <Building2 size={18} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                                Firma & Person
                            </div>
                            <div className="row g-3">
                                <div className="col-12">
                                    <label style={labelStyle}>Firma<RequiredMark /></label>
                                    <input
                                        type="text"
                                        value={formData.company || ''}
                                        onChange={(e) => handleChange('company', e.target.value)}
                                        style={showValidation ? requiredInputStyle(formData.company) : inputStyle}
                                        placeholder="Firmenname"
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label style={labelStyle}>Titel</label>
                                    <Select
                                        value={formData.title}
                                        onChange={(opt) => handleChange('title', opt)}
                                        options={titles}
                                        styles={customSelectStylesLarge}
                                        placeholder="Titel..."
                                        isClearable={false}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label style={labelStyle}>Anrede</label>
                                    <Select
                                        value={formData.gender}
                                        onChange={(opt) => handleChange('gender', opt)}
                                        options={genderOptions}
                                        styles={customSelectStylesLarge}
                                        placeholder="Anrede..."
                                        isClearable={false}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label style={labelStyle}>Vorname<RequiredMark /></label>
                                    <input
                                        type="text"
                                        value={formData.surname || ''}
                                        onChange={(e) => handleChange('surname', e.target.value)}
                                        style={showValidation ? requiredInputStyle(formData.surname) : inputStyle}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label style={labelStyle}>Nachname<RequiredMark /></label>
                                    <input
                                        type="text"
                                        value={formData.name || ''}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                        style={showValidation ? requiredInputStyle(formData.name) : inputStyle}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Anschrift */}
                    <div className="card" style={cardStyle}>
                        <div className="card-body">
                            <div style={sectionHeaderStyle}>
                                <MapPin size={18} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                                Anschrift
                            </div>
                            <div className="row g-3">
                                <div className="col-md-8">
                                    <label style={labelStyle}>Straße</label>
                                    <input
                                        type="text"
                                        value={formData.street || ''}
                                        onChange={(e) => handleChange('street', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label style={labelStyle}>Hausnummer</label>
                                    <input
                                        type="text"
                                        value={formData.houseNumber || ''}
                                        onChange={(e) => handleChange('houseNumber', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label style={labelStyle}>PLZ</label>
                                    <input
                                        type="text"
                                        value={formData.zipCode || ''}
                                        onChange={(e) => handleChange('zipCode', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div className="col-md-5">
                                    <label style={labelStyle}>Ort</label>
                                    <input
                                        type="text"
                                        value={formData.city || ''}
                                        onChange={(e) => handleChange('city', e.target.value)}
                                        style={inputStyle}
                                    />
                                    <CityOptions options={cityOptions} onSelect={selectCityOption} />
                                </div>
                                <div className="col-md-4">
                                    <label style={labelStyle}>Land</label>
                                    <Select
                                        value={formData.country}
                                        onChange={(opt) => handleChange('country', opt)}
                                        options={countryOptions}
                                        styles={customSelectStylesLarge}
                                        placeholder="Land..."
                                        isClearable={false}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Kontakt */}
                    <div className="card" style={cardStyle}>
                        <div className="card-body">
                            <div style={sectionHeaderStyle}>
                                <Phone size={18} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                                Kontakt
                            </div>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label style={labelStyle}>Telefon</label>
                                    <input
                                        type="tel"
                                        value={formData.phone || ''}
                                        onChange={(e) => handleChange('phone', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label style={labelStyle}>Fax</label>
                                    <input
                                        type="tel"
                                        value={formData.fax || ''}
                                        onChange={(e) => handleChange('fax', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label style={labelStyle}>Mobil</label>
                                    <input
                                        type="tel"
                                        value={formData.mobil || ''}
                                        onChange={(e) => handleChange('mobil', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* E-Mail & Website */}
                    <div className="card" style={cardStyle}>
                        <div className="card-body">
                            <div style={sectionHeaderStyle}>
                                <Mail size={18} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                                E-Mail & Website
                            </div>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label style={labelStyle}>E-Mail<RequiredMark /></label>
                                    <input
                                        type="email"
                                        value={formData.email || ''}
                                        onChange={(e) => handleChange('email', e.target.value)}
                                        style={showValidation ? requiredInputStyle(formData.email) : inputStyle}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label style={labelStyle}>E-Mail Hochzeit</label>
                                    <input
                                        type="email"
                                        value={formData.emailWedding || ''}
                                        onChange={(e) => handleChange('emailWedding', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div className="col-12">
                                    <label style={labelStyle}>Website</label>
                                    <div className="d-flex gap-2">
                                        <select
                                            value={formData.protocol}
                                            onChange={(e) => handleChange('protocol', e.target.value)}
                                            style={{ ...inputStyle, width: '110px', flexShrink: 0 }}
                                        >
                                            <option value="https">https://</option>
                                            <option value="http">http://</option>
                                        </select>
                                        <input
                                            type="text"
                                            value={formData.web || ''}
                                            onChange={(e) => handleChange('web', e.target.value)}
                                            style={inputStyle}
                                            placeholder="www.example.com"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className="col-lg-4">
                    <div style={{ position: 'sticky', top: '1rem' }}>
                        {/* Bankverbindung */}
                        <div className="card" style={cardStyle}>
                            <div className="card-body">
                                <div style={sectionHeaderStyle}>
                                    <CreditCard size={18} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                                    Bankverbindung
                                </div>
                                <div className="mb-3">
                                    <label style={labelStyle}>Bank</label>
                                    <input
                                        type="text"
                                        value={formData.bank || ''}
                                        onChange={(e) => handleChange('bank', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label style={labelStyle}>IBAN</label>
                                    <input
                                        type="text"
                                        value={formData.iban || ''}
                                        onChange={(e) => handleChange('iban', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label style={labelStyle}>BIC</label>
                                    <input
                                        type="text"
                                        value={formData.bic || ''}
                                        onChange={(e) => handleChange('bic', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div>
                                    <label style={labelStyle}>Kontoinhaber</label>
                                    <input
                                        type="text"
                                        value={formData.accountHolder || ''}
                                        onChange={(e) => handleChange('accountHolder', e.target.value)}
                                        style={inputStyle}
                                        placeholder="Optional, falls abweichend vom Firmennamen"
                                    />
                                    <small style={{ color: 'var(--text-secondary, #6c757d)', fontSize: '0.75rem', marginTop: '4px', display: 'block' }}>
                                        Wird im QR-Code als Empfängername verwendet. Leer lassen, um den Firmennamen zu nutzen.
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* Steuerliche Angaben */}
                        <div className="card" style={cardStyle}>
                            <div className="card-body">
                                <div style={sectionHeaderStyle}>
                                    <Receipt size={18} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                                    Steuerliche Angaben
                                </div>
                                <div className="mb-3">
                                    <label style={labelStyle}>Steuernummer</label>
                                    <input
                                        type="text"
                                        value={formData.taxNumber || ''}
                                        onChange={(e) => handleChange('taxNumber', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label style={labelStyle}>UStID</label>
                                    <input
                                        type="text"
                                        value={formData.vatNumber || ''}
                                        onChange={(e) => handleChange('vatNumber', e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div>
                                    <label style={labelStyle}>Umsatzsteuerpflicht</label>
                                    <Select
                                        value={formData.taxObligation}
                                        onChange={(opt) => handleChange('taxObligation', opt)}
                                        options={taxObligationOptions}
                                        styles={customSelectStylesLarge}
                                        placeholder="Auswählen..."
                                        isClearable={false}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Notizen */}
                        <div className="card" style={cardStyle}>
                            <div className="card-body">
                                <label style={labelStyle}>Notizen</label>
                                <textarea
                                    value={formData.notes || ''}
                                    onChange={(e) => handleChange('notes', e.target.value)}
                                    rows={4}
                                    style={{ ...inputStyle, resize: 'vertical' }}
                                    placeholder="Optionale Notizen..."
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Submit button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                {showValidation && !isValid() && (
                    <span style={{
                        fontSize: '0.875rem',
                        color: 'var(--danger, #dc3545)',
                        marginRight: 'auto',
                        alignSelf: 'center'
                    }}>
                        * Pflichtfelder ausfüllen
                    </span>
                )}
                <button
                    onClick={handleSubmit}
                    disabled={isSaving}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.625rem 1.5rem',
                        border: 'none',
                        borderRadius: '8px',
                        background: 'var(--accent-slate, #5b7c8d)',
                        color: 'white',
                        fontSize: '0.9375rem',
                        fontWeight: '600',
                        cursor: isSaving ? 'wait' : 'pointer',
                        opacity: isSaving ? 0.7 : 1,
                        transition: 'all 0.2s ease'
                    }}
                >
                    {isSaving ? 'Speichert...' : 'Speichern & Weiter'}
                </button>
            </div>
        </div>
    );
};

export default StepBasedata;
